/**
 * GS1 Parser — Ported from Flutter (gtin_parser.dart)
 *
 * Supports:
 * - GS1-128 (1D linear, starts with ]C1)
 * - DataMatrix (2D, starts with ]d2)
 * - Simple EAN-13 / GTIN-14 (13-14 numeric digits)
 * - GS1 strings with Application Identifiers (parenthesized or inline)
 * - Two-read scenarios (GTIN first, Lot/Expiry second)
 */

import { parseGS1Universal } from './gs1UniversalParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GS1ScanType =
  | 'twoReads'
  | 'gs1_128'
  | 'dataMatrix'
  | 'simpleEan'
  | 'unknown';

export interface GS1Data {
  gtin: string | null;
  lote: string | null;
  validade: Date | null;
  serie: string | null;
  dataProducao: Date | null;
  melhorUso: Date | null;
  quantidade: number | null;
  refCode: string | null;
  rawData: string;
  scanType: GS1ScanType;
  gtinValid: boolean;
}

// ---------------------------------------------------------------------------
// AI Definitions (from gs1_ai_definitions.dart)
// ---------------------------------------------------------------------------

/** Map of known GS1 Application Identifiers → fixed length (null = variable) */
const AI_LENGTHS: Record<string, number | null> = {
  '01': 14, '02': 14, '10': null, '11': 6, '15': 6, '17': 6,
  '20': 2, '21': null, '30': null, '37': null,
  '00': 18, '12': 6, '13': 6, '16': 6,
  '240': null, '241': null, '242': null, '243': null,
  '250': null, '251': null, '253': null, '254': null, '255': null,
  '310': 6, '311': 6, '312': 6, '313': 6, '314': 6, '315': 6,
  '316': 6, '320': 6, '321': 6, '322': 6, '323': 6, '324': 6,
  '325': 6, '326': 6, '327': 6, '328': 6, '329': 6, '330': 6,
  '331': 6, '332': 6, '333': 6, '334': 6, '335': 6, '336': 6,
  '340': 6, '341': 6, '342': 6, '343': 6, '344': 6, '345': 6,
  '346': 6, '347': 6, '348': 6, '349': 6, '350': 6, '351': 6,
  '352': 6, '353': 6, '354': 6, '355': 6, '356': 6, '357': 6,
  '360': 6, '361': 6, '362': 6, '363': 6, '364': 6, '365': 6,
  '366': 6, '367': 6, '368': 6, '369': 6,
  '390': null, '391': null, '392': null, '393': null,
  '394': null, '395': null,
  '410': 13, '411': 13, '412': 13, '413': 13, '414': 13, '415': 13,
  '416': 13, '417': 13,
  '420': null, '421': null, '422': 3, '423': null, '424': 3, '425': null,
  '426': 3, '427': null,
  '710': null, '711': null, '712': null, '713': null, '714': null, '715': null,
  '7001': 13, '7002': null, '7003': 10, '7004': null, '7005': null,
  '7006': 6, '7007': null, '7008': null, '7009': null, '7010': null,
  '8001': 14, '8002': null, '8003': null, '8004': null, '8005': 6,
  '8006': 18, '8007': null, '8008': null, '8009': null, '8010': null,
  '8011': null, '8012': null, '8013': null, '8017': 18, '8018': 18,
  '8019': null, '8020': null, '8026': 18, '8030': null, '8110': null,
  '8111': 4, '8112': null, '8200': null,
  '91': null, '92': null, '93': null, '94': null,
  '95': null, '96': null, '97': null, '98': null, '99': null,
};

export function hasKnownAI(ai: string): boolean {
  return ai in AI_LENGTHS;
}

export function getAILength(ai: string): number | null | undefined {
  return AI_LENGTHS[ai];
}

// ---------------------------------------------------------------------------
// GTIN Validation (Modulo-10)
// ---------------------------------------------------------------------------

export function normalizeGtin(gtin: string): string {
  if (gtin.length === 8) return '000000' + gtin;
  if (gtin.length === 12) return '00' + gtin;
  if (gtin.length === 13) return '0' + gtin;
  return gtin;
}

export function validateGtinChecksum(gtin: string): boolean {
  const normalized = normalizeGtin(gtin);
  if (normalized.length !== 14 || !/^\d+$/.test(normalized)) return false;

  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(normalized[i], 10);
    // GS1 GTIN-14 spec: posições pares (0,2,…,12) × 3, ímpares × 1.
    // (Paridade com Dart GtinValidator.validateChecksum — antes estava invertido,
    //  retornando false p/ TODO GTIN válido; o parser universal exige checksum ok.)
    sum += i % 2 === 0 ? digit * 3 : digit;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(normalized[13], 10);
}

// ---------------------------------------------------------------------------
// GS1 Date Parsing (YYMMDD)
// ---------------------------------------------------------------------------

export function parseGS1Date(yymmdd: string): Date | null {
  if (yymmdd.length !== 6 || !/^\d{6}$/.test(yymmdd)) return null;
  const yy = parseInt(yymmdd.substring(0, 2), 10);
  const mm = parseInt(yymmdd.substring(2, 4), 10);
  let dd = parseInt(yymmdd.substring(4, 6), 10);

  if (mm < 1 || mm > 12) return null;

  // GS1 spec: day 00 means last day of month
  // GS1 spec: yy 00-50 → 20xx, yy 51-99 → 19xx (synced with Flutter)
  const year = yy > 50 ? 1900 + yy : 2000 + yy;
  if (dd === 0) {
    dd = new Date(year, mm, 0).getDate();
  }

  return new Date(year, mm - 1, dd);
}

// ---------------------------------------------------------------------------
// AI Parsing
// ---------------------------------------------------------------------------

interface ParsedAIs {
  gtin: string | null;
  lote: string | null;
  validade: string | null;
  serie: string | null;
  dataProducao: string | null;
  melhorUso: string | null;
  quantidade: string | null;
  refCode: string | null;
}

function parseAIsWithParentheses(data: string): ParsedAIs {
  const result: ParsedAIs = {
    gtin: null, lote: null, validade: null,
    serie: null, dataProducao: null, melhorUso: null, quantidade: null, refCode: null,
  };

  const regex = /\((\d{2,4})\)([^()]+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(data)) !== null) {
    const ai = match[1];
    const value = match[2];
    assignAIValue(result, ai, value);
  }
  return result;
}

function assignAIValue(result: ParsedAIs, ai: string, value: string) {
  switch (ai) {
    case '01':
      result.gtin = value.length === 13 ? '0' + value : value;
      break;
    case '02':
      if (!result.gtin) result.gtin = value.length === 13 ? '0' + value : value;
      break;
    case '10': result.lote = value; break;
    case '11': result.dataProducao = value; break;
    case '15': result.melhorUso = value; break;
    case '17': result.validade = value; break;
    case '21': result.serie = value; break;
    case '30': case '37': result.quantidade = value; break;
    case '240': result.refCode = value; break;
  }
}

/** Validate if position is truly the start of an AI */
function isValidAIStartStrict(data: string, pos: number, ai: string): boolean {
  const aiLength = getAILength(ai);

  if (aiLength != null) {
    const expectedEnd = pos + ai.length + aiLength;
    if (expectedEnd > data.length) return false;

    const valueAfterAI = data.substring(pos + ai.length, expectedEnd);
    if (!/^\d+$/.test(valueAfterAI)) return false;

    // Date AIs: validate month is plausible
    if (['11', '13', '15', '16', '17'].includes(ai) && valueAfterAI.length === 6) {
      const mm = parseInt(valueAfterAI.substring(2, 4), 10);
      if (mm < 1 || mm > 12) return false;
    }
    return true;
  }

  return pos + ai.length < data.length;
}

/** True if `pos` is a plausible start of the next AI (end-of-string, FNC1, or a
 *  known/valid AI). Mirrors Dart gs1_ai_parser._isLikelyNextAIStart. */
function isLikelyNextAIStart(data: string, pos: number): boolean {
  if (pos >= data.length) return true;
  if (data[pos] === '|') return true;
  for (const len of [2, 3, 4]) {
    if (pos + len <= data.length) {
      const cand = data.substring(pos, pos + len);
      if (hasKnownAI(cand) && isValidAIStartStrict(data, pos, cand)) return true;
    }
  }
  return false;
}

/** Find end of a variable-length AI value */
function findValueEnd(data: string, startIndex: number, currentAI?: string, sizeHint?: number | null): number {
  let end = data.length;

  const pipeIndex = data.indexOf('|', startIndex);
  if (pipeIndex !== -1 && pipeIndex < end) end = pipeIndex;

  // Story 2.23 V2 — FAST-PATH SIZE HINT (synced with Dart gs1_ai_parser._findValueEnd)
  // Corte exato de lote (AI 10) / serial (AI 21) usando o tamanho esperado vindo
  // dos itens da contagem. Valida que após o corte vem FNC1/AI conhecido/fim; senão
  // cai na heuristica abaixo (identica ao offline).
  if (sizeHint != null && (currentAI === '10' || currentAI === '21')) {
    const hintedEnd = startIndex + sizeHint;
    if (hintedEnd <= end && isLikelyNextAIStart(data, hintedEnd)) {
      return hintedEnd;
    }
  }

  // For lot (10) and serial (21): read up to 20 chars, break only on date AIs
  // Synced with Dart gs1_ai_parser.dart _findValueEnd
  if (currentAI === '10' || currentAI === '21') {
    const maxEnd = Math.min(startIndex + 20, end);
    const dateAIs = ['11', '13', '15', '17'];
    const complementaryAI = currentAI === '10' ? '21' : '10';

    for (let i = startIndex; i < maxEnd; i++) {
      if (i + 2 <= data.length) {
        const candidate = data.substring(i, i + 2);

        // 1. Date AIs with valid date
        if (dateAIs.includes(candidate) && isValidAIStartStrict(data, i, candidate)) {
          return i;
        }

        // 2. Complementary AI (10↔21) with letter→digit transition OR charsRead >= 3
        //    Synced with Flutter: when HID scanner strips GS separator between AIs,
        //    serial ending in digit + lot starting with digit won't have letter→digit
        //    transition. charsRead >= 3 catches this case safely.
        if (candidate === complementaryAI && i > startIndex) {
          const charsRead = i - startIndex;
          const prevChar = data[i - 1];
          const isLetterToDigit = /[A-Za-z]/.test(prevChar);
          if ((isLetterToDigit || charsRead >= 3) && isValidAIStartStrict(data, i, candidate)) {
            return i;
          }
        }

        // 3. AI 20 (variant, fixed 2 digits) as terminator
        //    Guard: charsRead >= 5 — avoids false positives like "LOT202613002"
        //    Synced with Flutter: strict look-ahead after AI 20 value
        if (candidate === '20') {
          const charsRead = i - startIndex;
          if (charsRead >= 5 && isValidAIStartStrict(data, i, candidate)) {
            // Strict look-ahead: after AI 20 + 2-digit value, must see
            // end-of-string, FNC1 (pipe), date AI, or complementary AI
            const afterAI20 = i + 4; // "20" (2) + value (2)
            let validAfterAI20 = afterAI20 >= data.length || data[afterAI20] === '|';
            // Guard for long serials ending in digits (false positive)
            if (validAfterAI20 && afterAI20 >= data.length && i > startIndex) {
              const prevCh = data[i - 1];
              const isLetterBefore = /[A-Za-z]/.test(prevCh);
              if (!isLetterBefore && charsRead >= 12) {
                validAfterAI20 = false; // digit→"20" in long value = likely false positive
              }
            }
            if (!validAfterAI20 && afterAI20 + 2 <= data.length) {
              const nextCandidate = data.substring(afterAI20, afterAI20 + 2);
              const trustedAIs = ['11', '13', '15', '17', complementaryAI];
              if (trustedAIs.includes(nextCandidate)) {
                validAfterAI20 = isValidAIStartStrict(data, afterAI20, nextCandidate);
              }
            }
            if (validAfterAI20) {
              return i;
            }
          }
        }
      }

      // 4. AI 240 (3 digits) as terminator — product without GTIN (Medtronic pattern)
      //    Guard: require letter→digit transition before "240" to avoid false positives.
      //    Serial numbers like "S24TZAMAS200024030" contain "240" mid-value.
      //    Real AI 240 after lot/serial is extremely rare (Medtronic uses 240 INSTEAD of GTIN,
      //    not alongside serial). Without FNC1, we can't reliably distinguish — be conservative.
      if (i + 3 <= data.length && i > startIndex) {
        const candidate3 = data.substring(i, i + 3);
        if (candidate3 === '240') {
          const prevCh = data[i - 1];
          const hasLetterTransition = /[A-Za-z]/.test(prevCh);
          if (hasLetterTransition && isValidAIStartStrict(data, i, candidate3)) {
            return i;
          }
        }
      }
    }
    return maxEnd;
  }

  // Standard heuristic for other variable-length AIs
  const dateAIs = ['11', '13', '15', '17'];
  const commonAIs = ['01', '10', '17', '21', '11', '15'];

  for (let i = startIndex; i < end; i++) {
    const hasLetterBefore = i > startIndex && /[A-Za-z]/.test(data[i - 1]);
    const isDigit = /\d/.test(data[i]);
    const isTransition = hasLetterBefore && isDigit;
    const charsRead = i - startIndex;

    if (i + 2 <= data.length) {
      const candidate = data.substring(i, i + 2);
      if (hasKnownAI(candidate)) {
        if (dateAIs.includes(candidate) && isValidAIStartStrict(data, i, candidate)) return i;
        if (commonAIs.includes(candidate) && isTransition && isValidAIStartStrict(data, i, candidate)) return i;
        if (i === startIndex && isValidAIStartStrict(data, i, candidate)) return i;
        if (charsRead > 15 && isValidAIStartStrict(data, i, candidate)) return i;
      }
    }

    if (i + 3 <= data.length && (isTransition || charsRead > 15)) {
      const candidate = data.substring(i, i + 3);
      if (hasKnownAI(candidate) && isValidAIStartStrict(data, i, candidate)) return i;
    }
    if (i + 4 <= data.length && (isTransition || charsRead > 15)) {
      const candidate = data.substring(i, i + 4);
      if (hasKnownAI(candidate) && isValidAIStartStrict(data, i, candidate)) return i;
    }
  }

  return end;
}

/** Lookup do tamanho esperado de lote (AI '10') / serial (AI '21') por GTIN,
 *  vindo dos itens da contagem — espelha o sizeHintLookup do Dart GS1Parser.parse. */
export type SizeHintLookup = (gtin: string, ai: '10' | '21') => number | null | undefined;

/** Fast-path Medtronic determinístico — porta de gs1_ai_parser.dart:_tryMedtronicFastPath.
 *  Cobre padrões empíricos confirmados (REF termina em X, lote = 10 dígitos). */
function tryMedtronicFastPath(data: string): ParsedAIs | null {
  const stripped = data.startsWith('|') ? data.substring(1) : data;
  const empty: ParsedAIs = {
    gtin: null, lote: null, validade: null,
    serie: null, dataProducao: null, melhorUso: null, quantidade: null, refCode: null,
  };

  // Caso 1: AI 10 isolado com EXATAMENTE 10 dígitos
  const loteOnly = /^10(\d{10})$/.exec(stripped);
  if (loteOnly) return { ...empty, lote: loteOnly[1] };

  // Caso 2: AI 240 + AI 10 concatenados (verso da caixa)
  const concat = /^240([A-Z][A-Z0-9]{3,28}X)10(\d{10})(?:20(\d{2}))?$/.exec(stripped);
  if (concat) return { ...empty, refCode: concat[1], lote: concat[2] };

  // Caso 3: GS1-128 LATERAL completo SEM FNC1 (01+17+10[+20])
  const lateral = /^01(\d{14})17(\d{6})10(\d{10})(?:20(\d{2}))?$/.exec(stripped);
  if (lateral) {
    const val6 = lateral[2];
    const mm = parseInt(val6.substring(2, 4), 10);
    const dd = parseInt(val6.substring(4, 6), 10);
    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return { ...empty, gtin: lateral[1], validade: val6, lote: lateral[3] };
    }
  }

  return null;
}

/** Converte o Map AI→valor do parser universal para o shape ParsedAIs.
 *  Espelha gs1_ai_parser.dart:_mapAIsToFields. */
function mapUniversalToParsed(ais: Record<string, string>): ParsedAIs {
  const gtinRaw = ais['01'] ?? ais['02'] ?? null;
  return {
    gtin: gtinRaw ? (gtinRaw.length === 13 ? '0' + gtinRaw : gtinRaw) : null,
    lote: ais['10'] ?? null,
    validade: ais['17'] ?? null,
    serie: ais['21'] ?? null,
    dataProducao: ais['11'] ?? null,
    melhorUso: ais['15'] ?? null,
    quantidade: ais['30'] ?? ais['37'] ?? null,
    refCode: ais['240'] ?? null,
  };
}

/** Espelha gs1_ai_parser.dart:parseAIsUniversal — parênteses → fast-path Medtronic
 *  → universal (backtracking+scoring) → heurística sequencial (fallback c/ size hint). */
function parseAIs(data: string, sizeHintLookup?: SizeHintLookup): ParsedAIs {
  if (data.includes('(') && data.includes(')')) {
    return parseAIsWithParentheses(data);
  }

  const fast = tryMedtronicFastPath(data);
  if (fast) return fast;

  const universal = parseGS1Universal(data);
  if (universal && Object.keys(universal).length > 0) {
    return mapUniversalToParsed(universal);
  }

  return parseAIsSequential(data, sizeHintLookup);
}

/** Heurística sequencial original (fallback). Mantém o size hint (AI 10/21). */
function parseAIsSequential(data: string, sizeHintLookup?: SizeHintLookup): ParsedAIs {
  const result: ParsedAIs = {
    gtin: null, lote: null, validade: null,
    serie: null, dataProducao: null, melhorUso: null, quantidade: null, refCode: null,
  };

  let pos = 0;
  while (pos < data.length) {
    let ai: string | null = null;
    let aiLength: number | null | undefined;

    // Try 4-digit AIs first, then 3, then 2
    for (const len of [4, 3, 2]) {
      if (pos + len <= data.length) {
        const candidate = data.substring(pos, pos + len);
        if (hasKnownAI(candidate)) {
          ai = candidate;
          aiLength = getAILength(candidate);
          break;
        }
      }
    }

    if (!ai) { pos++; continue; }

    const valueStart = pos + ai.length;
    if (valueStart >= data.length) break;

    let value: string;
    let valueEnd: number;

    if (aiLength != null) {
      valueEnd = Math.min(valueStart + aiLength, data.length);
      value = data.substring(valueStart, valueEnd);
    } else {
      // Story 2.23 V2: size hint p/ lote (10) e serial (21) quando ha GTIN ja lido
      let sizeHint: number | null | undefined;
      if (sizeHintLookup && (ai === '10' || ai === '21') && result.gtin) {
        sizeHint = sizeHintLookup(result.gtin, ai);
      }
      valueEnd = findValueEnd(data, valueStart, ai, sizeHint);
      value = data.substring(valueStart, valueEnd);
    }

    assignAIValue(result, ai, value);
    pos = valueEnd;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Main Parse function
// ---------------------------------------------------------------------------

export function parseGS1(raw: string, sizeHintLookup?: SizeHintLookup): GS1Data {
  let data = raw.trim();
  let scanType: GS1ScanType = 'unknown';

  // Detect and remove symbology identifiers
  // Accept both ] and [ prefixes: some HID scanners on ABNT2 keyboard layout
  // send Ctrl+] which produces [ instead of ]
  if (data.startsWith(']d2') || data.startsWith('[d2')) {
    data = data.substring(3);
    scanType = 'dataMatrix';
  } else if (data.startsWith(']C1') || data.startsWith('[C1')) {
    data = data.substring(3);
    scanType = 'gs1_128';
  } else if (data.startsWith(']E0') || data.startsWith('[E0')) {
    data = data.substring(3);
    scanType = 'simpleEan';
  }

  // Normalize FNC1 substitutes to pipe separator
  // \x1D = ASCII 29 (GS char, standard FNC1)
  // [ ]  = FNC1 via HID keyboard wedge (ABNT2 and US layouts)
  // ~    = FNC1 substitute on some Honeywell/Zebra scanners
  // Safe: [ ] ~ are NOT valid characters in GS1 AI data values (Section 7.11)
  data = data.replace(/[\x1D\[\]~]/g, '|');

  // Simple EAN-13 / GTIN-14
  if (/^\d{13,14}$/.test(data)) {
    const normalized = normalizeGtin(data);
    return {
      gtin: normalized,
      lote: null,
      validade: null,
      serie: null,
      dataProducao: null,
      melhorUso: null,
      quantidade: null,
      refCode: null,
      rawData: raw,
      scanType: 'simpleEan',
      gtinValid: validateGtinChecksum(normalized),
    };
  }

  // Parse AI string
  const parsed = parseAIs(data, sizeHintLookup);

  // Scan type detection — synced with Flutter ScanTypeDetector
  // refCode FIRST: any barcode with AI 240 is always twoReads (Medtronic pattern)
  if (scanType === 'unknown') {
    if (parsed.refCode) scanType = 'twoReads';
    else if (parsed.gtin && parsed.lote) scanType = 'dataMatrix';
    else if (parsed.gtin && !parsed.lote) scanType = 'simpleEan';
    else if (!parsed.gtin && parsed.lote) scanType = 'twoReads';
  }

  let gtinValid = true;
  if (parsed.gtin) gtinValid = validateGtinChecksum(parsed.gtin);

  return {
    gtin: parsed.gtin,
    lote: parsed.lote,
    validade: parsed.validade ? parseGS1Date(parsed.validade) : null,
    serie: parsed.serie,
    dataProducao: parsed.dataProducao ? parseGS1Date(parsed.dataProducao) : null,
    melhorUso: parsed.melhorUso ? parseGS1Date(parsed.melhorUso) : null,
    quantidade: parsed.quantidade ? parseInt(parsed.quantidade, 10) : null,
    refCode: parsed.refCode,
    rawData: raw,
    scanType,
    gtinValid,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check if scan result is complete — synced with Flutter */
export function isComplete(data: GS1Data): boolean {
  return (!!data.gtin && !!data.lote && !!data.validade) || (!!data.refCode && !!data.lote);
}

/** Check if scan needs a second read (has GTIN or refCode but no lot) — synced with Flutter */
export function needsSecondRead(data: GS1Data): boolean {
  return (!!data.gtin || !!data.refCode) && !data.lote;
}

/** Check if scan is a second read (has lot but no GTIN and no refCode) — synced with Flutter */
export function isSecondRead(data: GS1Data): boolean {
  return !data.gtin && !data.refCode && !!data.lote;
}

/** Merge two GS1Data (for two-read scenarios) */
export function mergeGS1Data(first: GS1Data, second: GS1Data): GS1Data {
  return {
    gtin: first.gtin ?? second.gtin,
    lote: first.lote ?? second.lote,
    validade: first.validade ?? second.validade,
    serie: first.serie ?? second.serie,
    dataProducao: first.dataProducao ?? second.dataProducao,
    melhorUso: first.melhorUso ?? second.melhorUso,
    quantidade: first.quantidade ?? second.quantidade,
    refCode: first.refCode ?? second.refCode,
    rawData: `${first.rawData}|${second.rawData}`,
    scanType: 'twoReads',
    gtinValid: first.gtinValid && second.gtinValid,
  };
}

/** Validate merge compatibility — ported from Flutter gtin_parser.dart */
export function canMerge(first: GS1Data, second: GS1Data): boolean {
  const firstHasProduct = !!first.gtin || !!first.refCode;
  const firstMissingLot = !first.lote;
  const secondHasLot = !!second.lote;
  const secondNoProduct = !second.gtin && !second.refCode;
  return firstHasProduct && firstMissingLot && secondHasLot && secondNoProduct;
}

/** Get effective lote: serializado uses serie as lote — ported from Flutter GS1Data */
export function getEffectiveLote(data: GS1Data, serializadoSn: boolean): string | null {
  if (serializadoSn && data.serie) return data.serie;
  return data.lote;
}

/** Format Date to GS1 YYMMDD string — ported from Flutter GS1DateParser.format */
export function formatGS1Date(date: Date): string {
  const yy = date.getFullYear().toString().slice(-2);
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  const dd = date.getDate().toString().padStart(2, '0');
  return `${yy}${mm}${dd}`;
}
